/****************
 * SIGN TYPED DATA IN THE PERSON'S OWN BROWSER WALLET. 2026-11-03.
 *
 * The key screen's "Approve CopyFi's fee" button hands the wallet a payload the
 * server built and asks it to sign (EIP-712, eth_signTypedData_v4). What comes
 * back is a signature; no key is ever read, held or sent. Works with a wallet
 * that injects window.ethereum (MetaMask, Rabby, Brave, Coinbase Wallet, or a
 * wallet's own in-app browser). A wallet reached only through WalletConnect, and
 * the embedded wallet behind an email login, are not covered.
 *
 * TWO THINGS A WALLET INSISTS ON, MET HERE:
 *   - the account that signs must be the one the key belongs to: asked for, and
 *     refused with the address to switch to when it is not there;
 *   - eth_signTypedData_v4 refuses a payload whose domain chainId differs from
 *     the network the wallet is on. Hyperliquid's approval is signed under chain
 *     0x66eee (Arbitrum Sepolia), so the wallet is switched to it for the
 *     signature (added first if it does not know it) and put back afterwards.
 ****************/

export type WalletErrorCode = 'no_wallet' | 'rejected' | 'wrong_account' | 'failed';

export class WalletError extends Error {
  code: WalletErrorCode;
  detail?: string;
  constructor(code: WalletErrorCode, detail?: string) {
    super(code + (detail ? `: ${detail}` : ''));
    this.code = code;
    this.detail = detail;
  }
}

export interface Eip1193 {
  request(args: { method: string; params?: unknown[] | object }): Promise<unknown>;
}

export interface WalletSignature {
  r: string;
  s: string;
  v: number;
}

/** The wallet this browser exposes, if any. */
export function injectedWallet(): Eip1193 | null {
  const w = (globalThis as { ethereum?: Eip1193 }).ethereum;
  return w && typeof w.request === 'function' ? w : null;
}

/** r, s and v from the 65-byte hex a wallet returns. */
export function splitSignature(sig: string): WalletSignature {
  const hex = sig.trim().replace(/^0x/i, '');
  if (!/^[0-9a-fA-F]{130}$/.test(hex)) throw new WalletError('failed', 'the wallet returned a signature that is not 65 bytes');
  return { r: '0x' + hex.slice(0, 64), s: '0x' + hex.slice(64, 128), v: parseInt(hex.slice(128, 130), 16) };
}

function codeOf(e: unknown): number | undefined {
  return typeof e === 'object' && e !== null && 'code' in e ? Number((e as { code: unknown }).code) : undefined;
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : typeof e === 'object' && e !== null && 'message' in e ? String((e as { message: unknown }).message) : String(e);
}

// Arbitrum Sepolia, the network Hyperliquid's approvals are signed under.
const ARBITRUM_SEPOLIA = {
  chainId: '0x66eee',
  chainName: 'Arbitrum Sepolia',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: ['https://sepolia-rollup.arbitrum.io/rpc'],
  blockExplorerUrls: ['https://sepolia.arbiscan.io'],
};

async function switchChain(wallet: Eip1193, chainId: string): Promise<void> {
  try {
    await wallet.request({ method: 'wallet_switchEthereumChain', params: [{ chainId }] });
  } catch (e) {
    // 4902: the wallet has never heard of this network. Add it, which also switches.
    if (codeOf(e) === 4902 && chainId.toLowerCase() === ARBITRUM_SEPOLIA.chainId) {
      await wallet.request({ method: 'wallet_addEthereumChain', params: [ARBITRUM_SEPOLIA] });
      return;
    }
    throw e;
  }
}

/**
 * Ask the wallet to sign `typedData` as `expectedAccount`. Throws WalletError:
 * no_wallet (nothing injected), rejected (the person said no), wrong_account
 * (detail = the address to switch to), failed (anything else, detail = why).
 */
export async function signTypedDataV4(
  wallet: Eip1193 | null,
  expectedAccount: string,
  typedData: Record<string, unknown>,
): Promise<WalletSignature> {
  if (!wallet) throw new WalletError('no_wallet');
  let previousChain: string | undefined;
  let switched = false;
  try {
    const accounts = (await wallet.request({ method: 'eth_requestAccounts' })) as string[];
    const want = expectedAccount.toLowerCase();
    const account = accounts.find((a) => a.toLowerCase() === want);
    if (!account) throw new WalletError('wrong_account', expectedAccount);

    const domain = (typedData.domain ?? {}) as { chainId?: string | number };
    if (domain.chainId !== undefined) {
      const target = typeof domain.chainId === 'number' ? '0x' + domain.chainId.toString(16) : String(domain.chainId);
      const current = String(await wallet.request({ method: 'eth_chainId' }));
      if (current.toLowerCase() !== target.toLowerCase()) {
        previousChain = current;
        await switchChain(wallet, target);
        switched = true;
      }
    }
    const signed = await wallet.request({ method: 'eth_signTypedData_v4', params: [account, JSON.stringify(typedData)] });
    return splitSignature(String(signed));
  } catch (e) {
    if (e instanceof WalletError) throw e;
    if (codeOf(e) === 4001) throw new WalletError('rejected');
    throw new WalletError('failed', messageOf(e));
  } finally {
    // Put the wallet back where the person had it. Best effort: failing to is not their problem.
    if (switched && previousChain) {
      try {
        await wallet.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: previousChain }] });
      } catch {
        /* left on the signing network; harmless */
      }
    }
  }
}
